import { Any } from "@cede/types";
import { SdkErrorCodes, SdkErrorMessages } from "./errorsMetadata";

/**
 * @description Base error for the SDK
 */
export class CedeSDKError extends Error {
  readonly isBaseError = true;
  exchangeId?: string;
  originalErrorMessage?: string;

  private metadata: Record<string, Any> = {};
  protected notReportable = false;
  private sentryName: string | undefined = undefined;
  sentry: Any;
  constructor(
    public code: number,
    public message: string,
  ) {
    super(message);
    this.name = "CedeSDKError";
  }

  addOriginalError(originalErrorMessage: string) {
    this.originalErrorMessage = originalErrorMessage;
    return this;
  }

  reportSentry() {
    const metadata = this.metadata;
    const sentry = this.sentry;
    // Clean metadata/sentry
    this.metadata = {};
    delete this.sentry;

    if (this.notReportable || !sentry) {
      return;
    }

    const clone = structuredClone(this);
    clone.name = this.sentryName || this.name;
    sentry.captureException(clone, {
      captureContext: {
        extra: {
          ...metadata,
          code: this.code,
          exchangeId: this.exchangeId,
        },
      },
    });
  }

  setSentryName(name: string) {
    this.sentryName = name;
  }

  setMetadata(metadata: Record<string, Any>) {
    this.metadata = metadata;
  }

  addMetadata(key: string, value: Any) {
    this.metadata[key] = value;
    return this;
  }

  overwriteMessage(message: string) {
    this.message = message;
    return this;
  }

  addMessage(message: string) {
    this.message = `${this.message}. ${message}`;
    return this;
  }

  exchange(exchangeId: string) {
    this.exchangeId = exchangeId;
    return this;
  }

  apply({ shouldPrependExchangeId = false } = {}) {
    if (shouldPrependExchangeId && this.exchangeId) {
      this.message = `${this.exchangeId}: ${this.message}`;
    }
    this.reportSentry();
    return this;
  }
}

/**
 * @description Default error for the SDK
 */
export class DefaultError extends CedeSDKError {
  constructor(code = SdkErrorCodes.DEFAULT_ERROR, message = SdkErrorMessages.DEFAULT_ERROR) {
    super(code, message);
    this.name = "DefaultError";
  }
}

/**
 * @description Thrown when the the provided method params are invalid
 */
export class InvalidParamsError extends DefaultError {
  constructor(description: string) {
    super(SdkErrorCodes.INVALID_PARAMS, SdkErrorMessages.INVALID_PARAMS(description));
    this.name = "InvalidParamsError";
  }
}
/**
 * @description Thrown when the cache can't be persisted
 */
export class InvalidCacheError extends DefaultError {
  constructor(key: string) {
    super(SdkErrorCodes.INVALID_CACHE, SdkErrorMessages.INVALID_CACHE(key));
    this.name = "InvalidCacheError";
  }
}

/**
 * @description Thrown when we try to use the external cache and it's not set
 */
export class FetchFromCacheError extends DefaultError {
  constructor(message: string) {
    super(SdkErrorCodes.FETCH_FROM_CACHE_ERROR, SdkErrorMessages.FETCH_FROM_CACHE_ERROR(message));
    this.name = "FetchFromCacheError";
  }
}

/**
 * @description Thrown when the address is invalid
 */
export class InvalidAddressError extends DefaultError {
  constructor() {
    super(SdkErrorCodes.INVALID_ADDRESS, SdkErrorMessages.INVALID_ADDRESS);
    this.name = "InvalidAddressError";
  }
}

export class RequestTimeout extends DefaultError {
  constructor(message: string) {
    super(SdkErrorCodes.REQUEST_TIMEOUT, SdkErrorMessages.REQUEST_TIMEOUT(message));
    this.name = "RequestTimeout";
  }
}

export class ThrottlerError extends DefaultError {
  constructor(message: string, metadata: Record<string, Any>) {
    super(SdkErrorCodes.THROTTLER_ERROR, message);
    this.name = "ThrottlerError";
    this.setMetadata(metadata);
  }
}
